import React from "react";
import { Search, X } from "lucide-react";

const FILTER_OPS = [
  { value: "contains",    label: "contains"    },
  { value: "equals",      label: "equals"      },
  { value: "starts_with", label: "starts with" },
  { value: "not_equals",  label: "≠"           },
  { value: "gt",          label: ">"           },
  { value: "lt",          label: "<"           },
];

export const EMPTY_TAB_FILTER = { text: "", field: "", op: "contains", value: "" };

/**
 * Apply a tab filter to one "cell" dict. Returns true if it passes.
 * The caller is responsible for building the flat dict it wants searched
 * (e.g. concat(row_a, row_b, rule_name, target_file_name, …)).
 */
export function rowPassesTabFilter(flatDict, filter) {
  const f = filter || EMPTY_TAB_FILTER;
  if (f.text && f.text.trim()) {
    const hay = Object.values(flatDict || {})
      .map(v => (v && typeof v === "object" ? JSON.stringify(v) : String(v ?? "")))
      .join(" ").toLowerCase();
    if (!hay.includes(f.text.toLowerCase())) return false;
  }
  if (f.field && f.value && f.value.trim()) {
    const raw = flatDict?.[f.field];
    const cell = (raw && typeof raw === "object" ? JSON.stringify(raw) : String(raw ?? "")).toLowerCase();
    const v = f.value.toLowerCase();
    if (f.op === "contains"    && !cell.includes(v))                return false;
    if (f.op === "equals"      && cell !== v)                       return false;
    if (f.op === "starts_with" && !cell.startsWith(v))              return false;
    if (f.op === "not_equals"  && cell === v)                       return false;
    if (f.op === "gt"          && !(parseFloat(cell) > parseFloat(v))) return false;
    if (f.op === "lt"          && !(parseFloat(cell) < parseFloat(v))) return false;
  }
  return true;
}

export default function TabSearchFilter({ filter, onChange, columns = [], testidPrefix = "tab-filter", visible = 0, total = 0 }) {
  const f = filter || EMPTY_TAB_FILTER;
  const active = !!(f.text || f.value);

  return (
    <div
      data-testid={`${testidPrefix}-bar`}
      style={{
        padding: "0.4rem 0.875rem",
        borderBottom: "1px solid var(--border-default)",
        background: "var(--bg-subtle, #FAFAF7)",
        display: "flex", alignItems: "center", gap: "0.45rem", flexWrap: "wrap",
      }}
    >
      <Search size={12} style={{ color: "var(--text-muted)", flexShrink: 0 }} />
      <input
        data-testid={`${testidPrefix}-text`}
        value={f.text}
        onChange={e => onChange({ ...f, text: e.target.value })}
        placeholder="Search all pages on server…"
        title="Server-side search across every record in this tab (~300ms debounce)"
        style={{ flex: "1 1 280px", minWidth: "140px", border: "none", outline: "none", fontSize: "0.78rem", background: "transparent" }}
      />
      {columns.length > 0 && (
        <>
          <span style={{ width: "1px", height: "14px", background: "var(--border-default)" }} />
          <select
            data-testid={`${testidPrefix}-field`}
            value={f.field}
            onChange={e => onChange({ ...f, field: e.target.value, value: "" })}
            style={{ fontSize: "0.72rem", border: "none", outline: "none", background: "transparent", color: f.field ? "var(--text-primary)" : "var(--text-muted)", maxWidth: "180px" }}
          >
            <option value="">Field…</option>
            {columns.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          {f.field && (
            <>
              <select
                data-testid={`${testidPrefix}-op`}
                value={f.op}
                onChange={e => onChange({ ...f, op: e.target.value })}
                style={{ fontSize: "0.72rem", border: "none", outline: "none", background: "transparent", maxWidth: "110px" }}
              >
                {FILTER_OPS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <input
                data-testid={`${testidPrefix}-value`}
                value={f.value}
                onChange={e => onChange({ ...f, value: e.target.value })}
                placeholder="value…"
                style={{ flex: "0 1 140px", minWidth: "80px", border: "none", outline: "none", fontSize: "0.78rem", background: "transparent" }}
              />
            </>
          )}
        </>
      )}
      {active && (
        <>
          <span data-testid={`${testidPrefix}-count`} style={{ fontSize: "0.68rem", color: "var(--text-muted)", whiteSpace: "nowrap" }}>
            {visible} / {total}
          </span>
          <button
            data-testid={`${testidPrefix}-clear`}
            onClick={() => onChange(EMPTY_TAB_FILTER)}
            style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", display: "flex", padding: 0 }}
            title="Clear filter"
          >
            <X size={13} />
          </button>
        </>
      )}
    </div>
  );
}
